import { useState, useEffect } from 'react';
import { useRunScenario } from '@/hooks/Scenarios/useRun';
import { useSaveScenario } from '@/hooks/Scenarios/useSave';
import { useLoadScenarioFromText } from '@/hooks/Scenarios/useLoad';
import { ScenarioLoaderModal } from './ScenarioLoaderModal';
import { ScenarioSaverModal } from './ScenarioSaverModal';
import { Scenario } from '@/types/scenario';
import { formatRawValue } from '@/util/questionnaire-computations';
import {
  LuFolderOpen,
  LuPlay,
  LuSquare,
  LuCircle,
  LuTrash2,
  LuFileText,
} from 'react-icons/lu';

interface ControlButtonProps {
  icon: React.ReactNode;
  onClick: () => void;
  disabled: boolean;
  title: string;
  color: string;
  hoverColor: string;
}

const ControlButton = ({
  icon,
  onClick,
  disabled,
  title,
  color,
  hoverColor,
}: ControlButtonProps) => (
  <button
    className={`w-12 h-12 flex items-center justify-center rounded-full text-white transition-colors ${
      disabled
        ? 'bg-slate-600 cursor-not-allowed'
        : `${color} ${hoverColor} cursor-pointer`
    }`}
    onClick={onClick}
    disabled={disabled}
    title={title}
  >
    {icon}
  </button>
);

const ScenarioInfo = ({
  name,
  description,
}: {
  name: string;
  description: string;
}) => (
  <div className="bg-[var(--dbg-bg-secondary)] p-3 rounded border border-[var(--dbg-border)]">
    <div className="space-y-2">
      {name && (
        <div className="text-sm">
          <span className="font-bold text-[var(--dbg-text)]"> {name}</span>
        </div>
      )}
      {description && (
        <div className="text-sm text-[var(--dbg-text-muted)]">
          <pre>{description}</pre>
        </div>
      )}
    </div>
  </div>
);

const ScenarioStatus = ({
  scenario,
  scenarioName,
  scenarioDescription,
}: {
  scenario: Scenario | null;
  scenarioName: string;
  scenarioDescription: string;
}) => {
  if (!scenario) {
    return (
      <div className="mt-4 p-3 bg-[var(--dbg-bg-secondary)] border border-[var(--dbg-border)] rounded-lg">
        <div className="text-xs text-[var(--dbg-text-secondary)] uppercase tracking-wide mb-2">
          Scenario Status
        </div>
        <div className="text-sm text-[var(--dbg-text-secondary)] text-center py-4">
          <div className="flex justify-center mb-2">
            <LuFileText className="size-8 text-[var(--dbg-text-dim)]" />
          </div>
          <div className="font-medium text-[var(--dbg-text-muted)]">
            No scenario loaded
          </div>
          <div className="text-xs mt-1">
            Click the load button to load a scenario
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-4 p-3 bg-[var(--dbg-bg-secondary)] border border-blue-900 rounded-lg">
      <div className="text-xs text-blue-400 uppercase tracking-wide mb-2">
        Scenario Details
      </div>
      <div className="space-y-3">
        {(scenarioName || scenarioDescription) && (
          <ScenarioInfo name={scenarioName} description={scenarioDescription} />
        )}
        {scenario?.patient && <PatientInfo patient={scenario.patient} />}
        {scenario?.consultation && (
          <ConsultationInfo consultation={scenario.consultation} />
        )}
        {scenario?.responses && scenario.responses.length > 0 && (
          <ResponsesSummary responses={scenario.responses} />
        )}
      </div>
    </div>
  );
};

const PatientInfo = ({ patient }: { patient: Scenario['patient'] }) => (
  <div className="bg-[var(--dbg-bg-secondary)] p-3 rounded border border-[var(--dbg-border)]">
    <div className="text-sm font-semibold text-[var(--dbg-text-muted)] mb-2">
      Patient
    </div>
    <div className="grid grid-cols-2 gap-2 text-sm text-[var(--dbg-text)]">
      <div>
        <span className="font-medium text-[var(--dbg-text-secondary)]">
          Name:
        </span>{' '}
        {patient.firstName} {patient.lastName}
      </div>
      <div>
        <span className="font-medium text-[var(--dbg-text-secondary)]">
          DOB:
        </span>{' '}
        {new Date(patient.dateOfBirth).toLocaleDateString()}
      </div>
      <div>
        <span className="font-medium text-[var(--dbg-text-secondary)]">
          Sex:
        </span>{' '}
        {patient.sex}
      </div>
      <div>
        <span className="font-medium text-[var(--dbg-text-secondary)]">
          Self:
        </span>{' '}
        {patient.self ? 'Yes' : 'No'}
      </div>
    </div>
  </div>
);

const ConsultationInfo = ({
  consultation,
}: {
  consultation: Scenario['consultation'];
}) => (
  <div className="bg-[var(--dbg-bg-secondary)] p-3 rounded border border-[var(--dbg-border)]">
    <div className="text-sm font-semibold text-[var(--dbg-text-muted)] mb-2">
      Consultation
    </div>
    <div className="text-sm text-[var(--dbg-text)]">
      <span className="font-medium text-[var(--dbg-text-secondary)]">
        Date:
      </span>{' '}
      {new Date(consultation.date).toLocaleString()}
    </div>
  </div>
);

const ResponsesSummary = ({
  responses,
}: {
  responses: Scenario['responses'];
}) => (
  <div className="bg-[var(--dbg-bg-secondary)] p-3 rounded border border-[var(--dbg-border)]">
    <div className="text-sm font-semibold text-[var(--dbg-text-muted)] mb-2">
      Responses
    </div>
    <div className="text-sm text-[var(--dbg-text-secondary)]">
      {responses.length} response page{responses.length !== 1 ? 's' : ''} with{' '}
      {responses.reduce((total, page) => total + page.length, 0)} total
      responses
    </div>
    <div className="mt-3 space-y-3">
      {responses.map((page, pageIndex) => (
        <div
          key={pageIndex}
          className="border-t border-[var(--dbg-border)] pt-2"
        >
          <div className="text-sm font-medium text-[var(--dbg-text-muted)] mb-2">
            Page {pageIndex + 1} ({page.length} response
            {page.length !== 1 ? 's' : ''})
          </div>
          <div className="grid grid-cols-4 gap-3">
            {page.map(
              (
                response: Scenario['responses'][0][0],
                responseIndex: number,
              ) => (
                <div
                  key={responseIndex}
                  className="bg-[var(--dbg-bg)] p-2 rounded text-xs border border-[var(--dbg-border)]"
                >
                  <div className="grid grid-cols-1 gap-1 text-[var(--dbg-text)]">
                    <div>
                      <span className="font-medium text-[var(--dbg-text-secondary)]">
                        Question ID:
                      </span>{' '}
                      {response.questionId}
                    </div>
                    <div>
                      <span className="font-medium text-[var(--dbg-text-secondary)]">
                        Page:
                      </span>{' '}
                      {response.currentPage}
                    </div>
                    <div>
                      <span className="font-medium text-[var(--dbg-text-secondary)]">
                        Section:
                      </span>{' '}
                      {response.currentSection}
                    </div>
                    <div>
                      <span className="font-medium text-[var(--dbg-text-secondary)]">
                        Responses:
                      </span>
                      <div className="mt-1 ml-2">
                        {Object.entries(response.responses).map(
                          ([key, value]) => (
                            <div
                              key={key}
                              className="text-[var(--dbg-text-secondary)]"
                            >
                              {key}:{' '}
                              {typeof value === 'boolean'
                                ? value
                                  ? 'True'
                                  : 'False'
                                : formatRawValue(value, '')}
                            </div>
                          ),
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ),
            )}
          </div>
        </div>
      ))}
    </div>
  </div>
);

export const Scenarios = () => {
  const [scenarioTxt, setScenarioTxt] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSaverModalOpen, setIsSaverModalOpen] = useState(false);
  const [savedScenarioTxt, setSavedScenarioTxt] = useState('');

  const { scenario, scenarioName, scenarioDescription, error } =
    useLoadScenarioFromText(scenarioTxt);
  const { run, stop, isRunning, log, delayMs, setDelayMs } = useRunScenario(
    scenario || undefined,
  );
  const { scenarioTxt: newScenarioTxt } = useSaveScenario();

  const handleLoadScenario = (newScenarioText: string) => {
    console.log(newScenarioText);
    setScenarioTxt(newScenarioText);
  };

  useEffect(() => {
    if (newScenarioTxt) {
      setSavedScenarioTxt(newScenarioTxt);
    }
  }, [newScenarioTxt]);

  const handleSave = () => {
    setIsSaverModalOpen(true);
  };

  return (
    <div className="bg-[var(--dbg-bg)] text-[var(--dbg-text)] flex flex-col p-4 gap-4 min-h-0">
      {/* Media Player Style Controls */}
      <div className="flex items-center justify-center gap-3 p-3 bg-[var(--dbg-bg-secondary)] rounded-lg border border-[var(--dbg-border)]">
        {/* Delay Slider */}
        <div className="flex items-center gap-2">
          <label className="text-xs font-medium text-[var(--dbg-text-muted)] whitespace-nowrap">
            Delay:
          </label>
          <input
            type="range"
            value={delayMs}
            onChange={(e) => setDelayMs(Number(e.target.value))}
            className="w-20 h-2 rounded-lg appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
            style={{
              background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${
                (delayMs / 3000) * 100
              }%, var(--dbg-border) ${
                (delayMs / 3000) * 100
              }%, var(--dbg-border) 100%)`,
            }}
            min="0"
            max="3000"
            step="250"
            disabled={isRunning}
          />
          <span className="text-xs text-[var(--dbg-text-secondary)] w-8">
            {delayMs}ms
          </span>
        </div>

        {/* Control Buttons */}
        <div className="flex items-center gap-3">
          <ControlButton
            icon={<LuFolderOpen className="size-5" />}
            onClick={() => setIsModalOpen(true)}
            disabled={isRunning}
            title={
              isRunning
                ? 'Cannot load while scenario is running'
                : 'Load Scenario'
            }
            color="bg-yellow-600"
            hoverColor="hover:bg-yellow-700"
          />

          <ControlButton
            icon={<LuPlay className="size-5" />}
            onClick={run}
            disabled={!scenario || isRunning}
            title={isRunning ? 'Running...' : 'Run Scenario'}
            color="bg-blue-600"
            hoverColor="hover:bg-blue-700"
          />

          <ControlButton
            icon={<LuSquare className="size-5" />}
            onClick={stop}
            disabled={!isRunning}
            title={!isRunning ? 'Not running' : 'Stop Scenario'}
            color="bg-red-600"
            hoverColor="hover:bg-red-700"
          />

          <ControlButton
            icon={<LuCircle className="size-5" />}
            onClick={handleSave}
            disabled={isRunning}
            title={isRunning ? 'Cannot save while running' : 'Save Scenario'}
            color="bg-red-600"
            hoverColor="hover:bg-red-700"
          />

          <ControlButton
            icon={<LuTrash2 className="size-5" />}
            onClick={() => setScenarioTxt('')}
            disabled={!scenario || isRunning}
            title={!scenario ? 'No scenario to clear' : 'Clear Scenario'}
            color="bg-slate-600"
            hoverColor="hover:bg-slate-500"
          />
        </div>
      </div>
      {/* Error Display */}
      {error && (
        <div className="mt-4 p-3 bg-red-900/20 border border-red-800 rounded-lg">
          <div className="text-xs text-red-400 uppercase tracking-wide mb-1">
            Parse Error
          </div>
          <div className="text-sm font-medium text-red-300">{error}</div>
        </div>
      )}

      {/* Latest Log Entries */}
      {log.length > 0 && (
        <div className="mt-4 p-3 bg-[var(--dbg-bg-secondary)] border border-[var(--dbg-border)] rounded-lg">
          <div className="text-xs text-[var(--dbg-text-secondary)] uppercase tracking-wide mb-1">
            Latest Status Entries
          </div>
          <div className="text-sm font-medium text-[var(--dbg-text)]">
            {log.slice(-3).map((entry: string, index: number) => (
              <div key={index} className="mb-1 last:mb-0">
                {entry}
              </div>
            ))}
          </div>
        </div>
      )}

      <ScenarioStatus
        scenario={scenario}
        scenarioName={scenarioName}
        scenarioDescription={scenarioDescription}
      />

      <ScenarioLoaderModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onLoad={handleLoadScenario}
      />

      <ScenarioSaverModal
        isOpen={isSaverModalOpen}
        onClose={() => setIsSaverModalOpen(false)}
        scenarioTxt={newScenarioTxt || savedScenarioTxt}
        onLoad={handleLoadScenario}
      />
    </div>
  );
};
